import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { TID } from "../constants/testIds";
import TermsAcceptModal from "./TermsAcceptModal";
import Avatar from "./Avatar";
import Logo from "./Logo";
import NotificationsCenter from "./NotificationsCenter";
import {
  Home, FileText, PlusCircle, Users, Building2, Shield,
  ClipboardCheck, ClipboardList, Settings, LogOut, User as UserIcon,
  Sparkles, ScrollText, Search, ChevronDown, HelpCircle,
  BookOpen, Grid3x3, LayoutDashboard, KeyRound,
  Workflow as WorkflowIcon, CreditCard, LifeBuoy, BadgeCheck,
  Library,
} from "lucide-react";

const roleLabel = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  hr: "HR Business Partner",
  manager: "People Manager",
  employee: "Employee",
};

const NavItem = ({ to, icon: Icon, label, testid, badge }) => (
  <NavLink to={to} className={({ isActive }) => "side-link " + (isActive ? "active" : "")} data-testid={testid}>
    <Icon size={16} strokeWidth={1.75} /> <span className="flex-1">{label}</span>
    {badge && <span className="text-[10px] font-mono opacity-70">{badge}</span>}
  </NavLink>
);

export default function AppLayout({ children }) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const profileRef = useRef(null);
  const appsRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (appsRef.current && !appsRef.current.contains(e.target)) setAppsOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/cases", { params: { q: searchQ, limit: 6 } });
        setSearchResults(data);
        setShowResults(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  if (!user) return null;

  const isSuper = user.role === "super_admin";
  const isAdmin = user.role === "company_admin";
  const isHR = user.role === "hr";
  const isManager = user.role === "manager";
  const isEmployee = user.role === "employee";

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-white flex">
      {/* One-time terms & disclaimer acceptance for first login */}
      <TermsAcceptModal />
      {/* Sidebar */}
      <aside className="sidebar w-60 flex flex-col shrink-0">
        <div className="px-5 py-5 border-b border-[color:var(--sidebar-border)]">
          <Logo variant="light" size={30} to="/" testid="sidebar-logo" className="mb-2"/>
          <div className="text-[9px] uppercase tracking-[0.16em] text-[color:var(--sidebar-text)] font-mono">
            Employee Relations
          </div>
        </div>

        <div className="py-3 flex-1 overflow-y-auto">
          {isSuper && (
            <>
              <div className="side-eyebrow">PropHR Console</div>
              <NavItem to="/super" icon={LayoutDashboard} label="Overview" testid={TID.navHome} />
              <NavItem to="/super/tenants" icon={Building2} label="Tenants" testid={TID.navTenants} />
              <NavItem to="/super/workflows" icon={WorkflowIcon} label="Workflow Templates" testid="nav-workflows" />
              <NavItem to="/super/enterprise-intelligence" icon={Sparkles} label="Enterprise Intelligence" testid={TID.navGodMode} />
              <NavItem to="/tickets" icon={LifeBuoy} label="Support Desk" testid="nav-tickets" />
              <NavItem to="/super/audit" icon={ClipboardList} label="Audit Log" testid={TID.navAudit} />
            </>
          )}

          {isAdmin && (
            <>
              <div className="side-eyebrow">Workspace</div>
              <NavItem to="/admin" icon={LayoutDashboard} label="Overview" testid={TID.navHome} />
              <NavItem to="/admin/users" icon={Users} label="Users" testid={TID.navUsers} />
              <NavItem to="/admin/approvals" icon={ClipboardCheck} label="Approvals" testid="nav-approvals" />
              <NavItem to="/policies" icon={ScrollText} label="Policies" testid={TID.navPolicies} />
              <NavItem to="/admin/workflows" icon={WorkflowIcon} label="Workflows" testid="nav-workflows" />
              <NavItem to="/monitoring" icon={ClipboardCheck} label="Monitoring" testid="nav-monitoring" />
              <NavItem to="/admin/enterprise-intelligence" icon={Sparkles} label="Enterprise Intelligence" testid={TID.navGodMode} />
              <NavItem to="/admin/audit" icon={ClipboardList} label="Audit Log" testid={TID.navAudit} />
              <div className="side-eyebrow mt-2">Cases</div>
              <NavItem to="/cases" icon={FileText} label="All Cases" testid={TID.navCases} />
              <div className="side-eyebrow mt-2">Organization</div>
              <NavItem to="/admin/resources" icon={Library} label="Resources" testid="nav-resources" />
              <NavItem to="/admin/company-profile" icon={BadgeCheck} label="Company Profile" testid="nav-company-profile" />
              <NavItem to="/admin/billing" icon={CreditCard} label="Billing" testid="nav-billing" />
              <NavItem to="/tickets" icon={LifeBuoy} label="Support" testid="nav-tickets" />
            </>
          )}

          {isHR && (
            <>
              <div className="side-eyebrow">HR Command</div>
              <NavItem to="/hr" icon={LayoutDashboard} label="Overview" testid={TID.navHome} />
              <NavItem to="/cases" icon={FileText} label="All Cases" testid={TID.navCases} />
              <NavItem to="/policies" icon={ScrollText} label="Policies" testid={TID.navPolicies} />
              <NavItem to="/monitoring" icon={ClipboardCheck} label="Monitoring" testid="nav-monitoring" />
              <NavItem to="/tickets" icon={LifeBuoy} label="Support" testid="nav-tickets" />
              <NavItem to="/hr/audit" icon={ClipboardList} label="Audit" testid={TID.navAudit} />
              <div className="side-eyebrow mt-2">Organization</div>
              <NavItem to="/hr/resources" icon={Library} label="Resources" testid="nav-resources" />
            </>
          )}

          {isManager && (
            <>
              <div className="side-eyebrow">Manager</div>
              <NavItem to="/manager" icon={LayoutDashboard} label="Overview" testid={TID.navHome} />
              <NavItem to="/cases" icon={FileText} label="Team Cases" testid={TID.navCases} />
              <NavItem to="/policies" icon={ScrollText} label="Policies" testid={TID.navPolicies} />
              <NavItem to="/tickets" icon={LifeBuoy} label="Support" testid="nav-tickets" />
            </>
          )}

          {(isEmployee || isManager) && (
            <>
              <div className="side-eyebrow mt-2">My Space</div>
              <NavItem to="/dashboard" icon={Home} label="Dashboard" testid={TID.navHome} />
              <NavItem to="/cases/new" icon={PlusCircle} label="Report a Case" testid={TID.navCreate} />
              <NavItem to="/cases" icon={FileText} label="My Cases" testid={TID.navCases} />
              {isEmployee && <NavItem to="/policies" icon={BookOpen} label="Policies" testid={TID.navPolicies} />}
            </>
          )}
        </div>

        <div className="border-t border-slate-800 px-4 py-3 text-[10px] font-mono text-slate-600">
          <NavLink
            to="/help"
            className={({ isActive }) => "side-link mb-2 " + (isActive ? "active" : "")}
            data-testid="nav-help"
          >
            <HelpCircle size={16} strokeWidth={1.75} /> <span className="flex-1">Help & Docs</span>
          </NavLink>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-slate-500">System</span>
            <span className="live-dot"></span>
          </div>
          <div>SOC 2 · GDPR · CCPA</div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* HubSpot-style top header */}
        <header className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-5 shrink-0 relative z-50">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">{tenant?.name || "PropHR Root"}</span>
              {tenant && (
                <span className="text-[10px] font-mono text-slate-500 border border-slate-200 px-1.5 py-0.5 uppercase tracking-wider">
                  {tenant.plan}
                </span>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-2xl mx-6 relative" ref={searchRef}>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
              <input
                type="text"
                value={searchQ}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search cases, policies, users…"
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all"
                data-testid="global-search-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-400 border border-slate-200 px-1 rounded bg-white">⌘K</span>
            </div>
            {showResults && searchResults.length > 0 && (
              <div className="absolute top-full mt-1 left-0 right-0 dropdown">
                <div className="text-[10px] font-mono text-slate-400 px-3 pt-2 uppercase tracking-wider">Cases</div>
                {searchResults.map((c) => (
                  <div key={c.id}
                    onClick={() => { navigate(`/cases/${c.id}`); setShowResults(false); setSearchQ(""); }}
                    className="dropdown-item flex-col items-start !gap-0.5">
                    <div className="text-slate-900 font-medium text-sm">{c.title}</div>
                    <div className="text-xs text-slate-500 font-mono">{c.case_number} · {c.category}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* AI status */}
            <div className="hidden xl:flex items-center gap-1.5 text-xs text-slate-500 font-mono border border-slate-200 px-2 py-1 rounded">
              <span className="live-dot ai-dot"></span> SENTINEL ANALYTICS · READY
            </div>

            {/* Apps grid (HubSpot style) */}
            <div ref={appsRef} className="relative">
              <button
                onClick={() => setAppsOpen(!appsOpen)}
                className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                data-testid="apps-menu-btn"
              >
                <Grid3x3 size={18} strokeWidth={1.75} className="text-slate-600" />
              </button>
              {appsOpen && (
                <div className="absolute right-0 top-full mt-1 dropdown">
                  <div className="text-[10px] font-mono text-slate-400 px-3 pt-2 uppercase tracking-wider">Quick access</div>
                  {[
                    { to: "/cases", icon: FileText, label: "Cases" },
                    { to: "/cases/new", icon: PlusCircle, label: "Report Case" },
                    { to: isSuper ? "/super/enterprise-intelligence" : isAdmin ? "/admin/enterprise-intelligence" : null, icon: Sparkles, label: "Enterprise Intelligence" },
                    { to: "/policies", icon: ScrollText, label: "Policies" },
                    { to: isAdmin ? "/admin/resources" : isHR ? "/hr/resources" : null, icon: BookOpen, label: "Resources" },
                    { to: isAdmin ? "/admin/approvals" : null, icon: ClipboardCheck, label: "Approvals" },
                    { to: "/profile", icon: UserIcon, label: "Profile" },
                    { to: "/settings", icon: Settings, label: "Settings" },
                  ].filter(x => x.to).map(x => (
                    <div key={x.label}
                      onClick={() => { navigate(x.to); setAppsOpen(false); }}
                      className="dropdown-item">
                      <x.icon size={14} strokeWidth={1.75} /> {x.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Help */}
            <button className="p-1.5 hover:bg-slate-100 rounded transition-colors" title="Help & docs" onClick={() => navigate("/help")}>
              <HelpCircle size={18} strokeWidth={1.75} className="text-slate-600" />
            </button>

            {/* Notifications */}
            <NotificationsCenter/>

            {/* Profile Dropdown */}
            <div ref={profileRef} className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 pl-2 pr-2.5 py-1 hover:bg-slate-100 rounded transition-colors"
                data-testid="profile-menu-btn"
              >
                <Avatar user={user} size={28} testid="header-avatar"/>
                <ChevronDown size={14} className="text-slate-500" strokeWidth={2} />
              </button>
              {profileOpen && (
                <div className="absolute right-0 top-full mt-1 dropdown">
                  <div className="px-3 py-3 border-b border-slate-200">
                    <div className="text-sm font-semibold text-slate-900">{user.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    <div className="mt-1.5"><span className="pill pill-closed">{roleLabel[user.role]}</span></div>
                  </div>
                  <div onClick={() => { navigate("/profile"); setProfileOpen(false); }} className="dropdown-item" data-testid="profile-menu-profile">
                    <UserIcon size={14} strokeWidth={1.75} /> My Profile
                  </div>
                  <div onClick={() => { navigate("/settings"); setProfileOpen(false); }} className="dropdown-item" data-testid="profile-menu-settings">
                    <Settings size={14} strokeWidth={1.75} /> Settings
                  </div>
                  {isAdmin && (
                    <div onClick={() => { navigate("/admin/approvals"); setProfileOpen(false); }} className="dropdown-item">
                      <ClipboardCheck size={14} strokeWidth={1.75} /> Approvals
                    </div>
                  )}
                  <div onClick={() => { navigate("/help"); setProfileOpen(false); }} className="dropdown-item">
                    <HelpCircle size={14} strokeWidth={1.75} /> Help & docs
                  </div>
                  <div className="border-t border-slate-200"></div>
                  <div onClick={() => { navigate("/security"); setProfileOpen(false); }} className="dropdown-item">
                    <KeyRound size={14} strokeWidth={1.75} /> Change password
                  </div>
                  <div onClick={doLogout} className="dropdown-item !text-red-700" data-testid={TID.navLogout}>
                    <LogOut size={14} strokeWidth={1.75} /> Sign out
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto grain relative bg-slate-50/40">{children}</div>

        {/* Enterprise footer */}
        <footer className="enterprise-footer px-6 py-5 shrink-0">
          <div className="max-w-7xl mx-auto flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Logo variant="light" size={22} testid="footer-logo"/>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--fg-on-dark-muted)]">Enterprise</span>
              </div>
              <div className="text-[11px] leading-relaxed text-[color:var(--fg-on-dark-muted)] max-w-lg">
                Confidential &amp; Privileged. All activity on this platform is logged for compliance with GDPR, CCPA, EEOC, SOC 2 Type II, ISO 27001, and your organization&apos;s retention &amp; privacy policies. Sentinel Analytics outputs are advisory only; final decisions require human review.
              </div>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-[11px]">
              <div>
                <div className="text-slate-400 uppercase tracking-widest mb-1.5 text-[9px]">Product</div>
                <div className="flex flex-col gap-1">
                  <a href="/cases" className="hover:underline">Cases</a>
                  <a href="/policies" className="hover:underline">Policies</a>
                </div>
              </div>
              <div>
                <div className="text-slate-400 uppercase tracking-widest mb-1.5 text-[9px]">Compliance</div>
                <div className="flex flex-col gap-1">
                  <a href="/help" className="hover:underline">Data privacy</a>
                  <a href="/help" className="hover:underline">Retention policy</a>
                  <a href="/help" className="hover:underline">Terms</a>
                </div>
              </div>
              <div>
                <div className="text-slate-400 uppercase tracking-widest mb-1.5 text-[9px]">Support</div>
                <div className="flex flex-col gap-1">
                  <a href="/help" className="hover:underline">Help Center</a>
                  <a href="mailto:support@prophr.com" className="hover:underline">support@prophr.com</a>
                </div>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto mt-5 pt-4 border-t border-slate-800 flex flex-wrap justify-between items-center gap-2 text-[10px] font-mono text-slate-600">
            <div>© 2026 PropHR, Inc. All rights reserved.</div>
            <div>SESSION · {user.email}</div>
          </div>
        </footer>
      </main>
    </div>
  );
}
